/**
 * 顾问陪跑 · 客户详情「定稿与印刷」（草案 A-2 ④⑤，PR⑤）—— 每本顾问书一块。
 *
 * 流程：发起定稿确认 → 把确认页发给客户（企微侧边栏在 PR⑦，先复制小程序路径）→ 客户在小程序点「确认定稿」
 *      → 代下印刷单（用书上的收货地址、消耗书自带的 1 本精装额度）→ 录快递单号。
 *
 * PDF（PR⑤b）：只能在这里带登录态下载，不生成任何可外传的链接；每次下载留痕。
 * 下载后以文件形式在企微里发给客户（草案 §4.3）。
 *
 * 没有「替客户确认定稿」：定稿只能由客户本人在确认页点（D5）。发起后书的内容一改，
 * 这次确认就失效，要重新发起——这里显示的状态是后端读时重算过的。
 */
import { useState } from 'react';
import {
  Alert, Button, Card, Descriptions, Empty, Form, Input, Modal, Popconfirm, Space, Table, Tag, Typography, message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PermissionGate } from '../../../components/PermissionGate';
import { PERMISSIONS } from '../../../contracts/permissions';
import {
  cancelFinalizeRequest,
  createAdvisorPrintOrder,
  downloadAdvisorPdf,
  getAdvisorPdfStatus,
  getWorkFulfillment,
  startAdvisorPdf,
  shipAdvisorPrintOrder,
  startFinalizeRequest,
  updateAdvisorPrintOrderAddress,
  updateWorkShippingAddress,
  type AdvisorPrintOrderDto,
  type FinalizeRequestStatus,
  type ShippingAddressDto,
} from '../../../api/advisor';

const { Text } = Typography;

function formatTs(ms: number | null): string {
  if (ms == null) return '-';
  return new Date(ms).toLocaleString('zh-CN', { hour12: false });
}

const REQUEST_META: Record<FinalizeRequestStatus, { color: string; label: string }> = {
  pending: { color: 'gold', label: '等客户确认' },
  confirmed: { color: 'green', label: '客户已确认定稿' },
  invalidated: { color: 'red', label: '内容有变动，已失效' },
  cancelled: { color: 'default', label: '已取消' },
};

const ORDER_STATUS_LABEL: Record<string, string> = {
  pending_payment: '待补款',
  paid: '已下单·待印刷',
  printing: '印刷中',
  shipped: '已发货',
  completed: '已完成',
  cancelled: '已取消',
};

const ADDRESS_FIELDS: Array<[keyof ShippingAddressDto, string]> = [
  ['receiverName', '收件人'],
  ['receiverPhone', '手机号'],
  ['receiverProvince', '省'],
  ['receiverCity', '市'],
  ['receiverDistrict', '区县'],
  ['receiverAddress', '详细地址'],
];

function addressText(a: Partial<Record<keyof ShippingAddressDto, string | null>> | null): string {
  if (!a || !a.receiverName) return '';
  const region = [a.receiverProvince, a.receiverCity, a.receiverDistrict].filter(Boolean).join(' ');
  return `${a.receiverName} ${a.receiverPhone ?? ''}　${region} ${a.receiverAddress ?? ''}`;
}

const errMsg = (e: any, fallback: string) => e?.response?.data?.message ?? fallback;

export function WorkFulfillmentPanel({ workId }: { workId: string }) {
  const queryClient = useQueryClient();
  const queryKey = ['advisor-work-fulfillment', workId];
  const query = useQuery({ queryKey, queryFn: () => getWorkFulfillment(workId), retry: false });
  const data = query.data;
  const refresh = () => queryClient.invalidateQueries({ queryKey });

  const [addressTarget, setAddressTarget] = useState<{ kind: 'work' } | { kind: 'order'; orderId: string } | null>(null);
  const [shipTarget, setShipTarget] = useState<string | null>(null);
  const [addressForm] = Form.useForm<ShippingAddressDto>();
  const [shipForm] = Form.useForm<{ company: string; trackingNumber: string }>();

  const startMutation = useMutation({
    mutationFn: () => startFinalizeRequest(workId),
    onSuccess: () => {
      message.success('已发起定稿确认，把确认页发给客户');
      refresh();
    },
    onError: (e) => {
      message.error(errMsg(e, '发起失败'));
    },
  });
  const cancelMutation = useMutation({
    mutationFn: cancelFinalizeRequest,
    onSuccess: () => {
      message.success('已取消');
      refresh();
    },
    onError: (e) => {
      message.error(errMsg(e, '取消失败'));
    },
  });
  const printMutation = useMutation({
    mutationFn: () => createAdvisorPrintOrder(workId),
    onSuccess: () => {
      message.success('印刷单已下');
      refresh();
    },
    // PDF 还在生成时后端回 409 + 「约 1–2 分钟后再点一次」，原样提示
    onError: (e) => {
      message.warning(errMsg(e, '下单失败'));
    },
  });
  const addressMutation = useMutation({
    mutationFn: async (v: ShippingAddressDto): Promise<void> => {
      if (addressTarget?.kind === 'order') await updateAdvisorPrintOrderAddress(addressTarget.orderId, v);
      else await updateWorkShippingAddress(workId, v);
    },
    onSuccess: () => {
      message.success('地址已更新');
      setAddressTarget(null);
      refresh();
    },
    onError: (e) => {
      message.error(errMsg(e, '保存失败'));
    },
  });
  const shipMutation = useMutation({
    mutationFn: (v: { company: string; trackingNumber: string }) => shipAdvisorPrintOrder(shipTarget!, v),
    onSuccess: () => {
      message.success('已录入快递信息');
      setShipTarget(null);
      refresh();
    },
    onError: (e) => {
      message.error(errMsg(e, '保存失败'));
    },
  });

  const pdfKey = ['advisor-work-pdf', workId];
  const pdfQuery = useQuery({
    queryKey: pdfKey,
    queryFn: () => getAdvisorPdfStatus(workId),
    retry: false,
    // 生成中每 10 秒看一眼，好了就停
    refetchInterval: (q) => (q.state.data?.generating ? 10_000 : false),
  });
  const pdf = pdfQuery.data;
  const startPdfMutation = useMutation({
    mutationFn: () => startAdvisorPdf(workId),
    onSuccess: () => {
      message.info('开始生成，约 1–2 分钟，好了会自动出现下载按钮');
      queryClient.invalidateQueries({ queryKey: pdfKey });
    },
    onError: (e) => {
      message.error(errMsg(e, '生成失败'));
    },
  });
  const downloadMutation = useMutation({
    mutationFn: (pdfId: string) => downloadAdvisorPdf(workId, pdfId, `${data?.title || '经年叙'}.pdf`),
    onError: (e) => {
      message.error(errMsg(e, '下载失败'));
    },
  });

  if (query.isError) {
    return <Alert type="error" showIcon message={errMsg(query.error, '交付状态加载失败')} />;
  }
  if (!data) return <Card loading />;

  const req = data.latestRequest;
  const hasOrders = data.printOrders.some((o) => o.status !== 'cancelled');
  const canStart = !data.finalized && req?.status !== 'pending';
  const canPrint = data.finalized && req?.status === 'confirmed' && !hasOrders;

  const openAddress = (target: NonNullable<typeof addressTarget>, initial: Partial<ShippingAddressDto> | null) => {
    addressForm.setFieldsValue((initial ?? {}) as ShippingAddressDto);
    setAddressTarget(target);
  };

  const orderColumns: ColumnsType<AdvisorPrintOrderDto> = [
    {
      title: '印刷单',
      render: (_, o) => (
        <Space direction="vertical" size={0}>
          <Text copyable={{ text: o.orderId }}>{o.orderId}</Text>
          <Text type="secondary">{formatTs(o.createdAt)}</Text>
        </Space>
      ),
    },
    { title: '状态', render: (_, o) => <Tag>{ORDER_STATUS_LABEL[o.status] ?? o.status}</Tag> },
    { title: '收货', render: (_, o) => addressText(o.receiver) || '-' },
    {
      title: '物流',
      render: (_, o) =>
        o.trackingNumber ? (
          <Space direction="vertical" size={0}>
            <Text>
              {o.logisticsCompany} <Text copyable>{o.trackingNumber}</Text>
            </Text>
            <Text type="secondary">发货 {formatTs(o.shippedAt)}</Text>
          </Space>
        ) : (
          '-'
        ),
    },
    {
      title: '',
      render: (_, o) =>
        o.status === 'cancelled' ? null : (
          <PermissionGate point={PERMISSIONS.ADVISOR_FULFILL}>
            <Space>
              {!o.trackingNumber && (
                <Button size="small" type="primary" onClick={() => { shipForm.resetFields(); setShipTarget(o.orderId); }}>
                  录入快递
                </Button>
              )}
              {o.status !== 'shipped' && o.status !== 'completed' && (
                <Button
                  size="small"
                  onClick={() =>
                    openAddress({ kind: 'order', orderId: o.orderId }, o.receiver as Partial<ShippingAddressDto>)
                  }
                >
                  改地址
                </Button>
              )}
            </Space>
          </PermissionGate>
        ),
    },
  ];

  return (
    <Card
      size="small"
      title={
        <Space>
          <span>《{data.title || '未命名'}》</span>
          <Tag color={data.finalized ? 'green' : 'blue'}>{data.finalized ? '已定稿' : '讲述中'}</Tag>
          {data.lifecycleStatus !== 'active' && <Tag color="red">客户已删除这本书</Tag>}
        </Space>
      }
      extra={<Text type="secondary">{data.chapterCount} 章 · 剩余精装额度 {data.printCreditCount} 本</Text>}
    >
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        {/* ① 定稿确认 */}
        <Descriptions size="small" column={1} title="定稿确认">
          <Descriptions.Item label="当前状态">
            {req ? (
              <Space wrap>
                <Tag color={REQUEST_META[req.status].color}>{REQUEST_META[req.status].label}</Tag>
                <Text type="secondary">
                  {req.status === 'confirmed'
                    ? `客户确认于 ${formatTs(req.confirmedAt)}`
                    : `发起于 ${formatTs(req.createdAt)}`}
                </Text>
              </Space>
            ) : (
              <Text type="secondary">还没发起</Text>
            )}
          </Descriptions.Item>
          {data.confirmPagePath && (
            <Descriptions.Item label="确认页（小程序路径）">
              <Text copyable>{data.confirmPagePath}</Text>
            </Descriptions.Item>
          )}
        </Descriptions>
        {req?.status === 'invalidated' && (
          <Alert type="warning" showIcon message="发起之后书的内容又改过（章节 / 封面 / 书名 / 题献 / 插图），这次确认已失效，请重新发起。" />
        )}
        <PermissionGate point={PERMISSIONS.ADVISOR_FULFILL}>
          <Space>
            {canStart && (
              <Popconfirm
                title="发起定稿确认？"
                description="发起后如果再改书的内容，这次确认会自动失效，需要重新发起。"
                onConfirm={() => startMutation.mutate()}
              >
                <Button type="primary" loading={startMutation.isPending}>
                  {req?.status === 'invalidated' || req?.status === 'cancelled' ? '重新发起定稿确认' : '发起定稿确认'}
                </Button>
              </Popconfirm>
            )}
            {req?.status === 'pending' && (
              <Popconfirm title="取消这次定稿确认？" onConfirm={() => cancelMutation.mutate(req.requestId)}>
                <Button danger loading={cancelMutation.isPending}>
                  取消确认
                </Button>
              </Popconfirm>
            )}
          </Space>
        </PermissionGate>

        {/* PDF：销售自己通读 / 以文件发给客户 */}
        <Descriptions size="small" column={1} title="全书 PDF">
          <Descriptions.Item label="最近一份">
            {pdfQuery.isError ? (
              <Text type="danger">{errMsg(pdfQuery.error, '加载失败')}</Text>
            ) : pdf?.latest ? (
              <Text>
                {pdf.latest.type === 'final' ? '付印版' : '预览版'}
                {pdf.latest.pageCount ? ` · ${pdf.latest.pageCount} 页` : ''} · 生成于 {formatTs(pdf.latest.createdAt)}
              </Text>
            ) : (
              <Text type="secondary">还没有</Text>
            )}
            {pdf?.generating && <Tag color="processing" style={{ marginLeft: 8 }}>生成中</Tag>}
          </Descriptions.Item>
        </Descriptions>
        <PermissionGate point={PERMISSIONS.ADVISOR_CONTENT_READ}>
          <Space wrap>
            {pdf?.latest && (
              <Button loading={downloadMutation.isPending} onClick={() => downloadMutation.mutate(pdf.latest!.pdfId)}>
                下载 PDF
              </Button>
            )}
            <Button
              disabled={pdf?.generating}
              loading={startPdfMutation.isPending}
              onClick={() => startPdfMutation.mutate()}
            >
              {pdf?.latest ? '按最新内容重新生成' : '生成 PDF'}
            </Button>
            <Text type="secondary">书改过之后，旧 PDF 不会自动更新；发给客户前先重新生成。下载会留痕。</Text>
          </Space>
        </PermissionGate>

        {/* ② 收货地址 */}
        <Descriptions size="small" column={1} title="收货地址">
          <Descriptions.Item label="书上的地址">
            <Space wrap>
              <Text>{addressText(data.shippingAddress) || '未填写'}</Text>
              {!hasOrders && (
                <PermissionGate point={PERMISSIONS.ADVISOR_FULFILL}>
                  <Button size="small" onClick={() => openAddress({ kind: 'work' }, data.shippingAddress)}>
                    修改
                  </Button>
                </PermissionGate>
              )}
            </Space>
          </Descriptions.Item>
        </Descriptions>

        {/* ③ 印刷与物流 */}
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Space>
            <Text strong>印刷与物流</Text>
            <PermissionGate point={PERMISSIONS.ADVISOR_FULFILL}>
              <Popconfirm
                title="代下印刷单？"
                description="用书上的收货地址、消耗书自带的 1 本精装额度。下单后进入印刷，不再支持退款。"
                onConfirm={() => printMutation.mutate()}
                disabled={!canPrint}
              >
                <Button type="primary" disabled={!canPrint} loading={printMutation.isPending}>
                  代下印刷单
                </Button>
              </Popconfirm>
            </PermissionGate>
            {!canPrint && !hasOrders && <Text type="secondary">客户在确认页确认定稿后才能下单</Text>}
          </Space>
          <Table<AdvisorPrintOrderDto>
            rowKey="orderId"
            size="small"
            scroll={{ x: 'max-content' }}
            dataSource={data.printOrders}
            columns={orderColumns}
            pagination={false}
            locale={{ emptyText: <Empty description="还没有印刷单" /> }}
          />
        </Space>
      </Space>

      <Modal
        title={addressTarget?.kind === 'order' ? '修改印刷单收货地址' : '修改书上的收货地址'}
        open={!!addressTarget}
        onCancel={() => setAddressTarget(null)}
        onOk={() => addressForm.submit()}
        confirmLoading={addressMutation.isPending}
        destroyOnHidden
      >
        <Form form={addressForm} layout="vertical" onFinish={(v) => addressMutation.mutate(v)}>
          {ADDRESS_FIELDS.map(([name, label]) => (
            <Form.Item key={name} name={name} label={label} rules={[{ required: true, whitespace: true, message: `请填写${label}` }]}>
              <Input maxLength={name === 'receiverAddress' ? 200 : 32} />
            </Form.Item>
          ))}
        </Form>
      </Modal>

      <Modal
        title="录入快递信息"
        open={!!shipTarget}
        onCancel={() => setShipTarget(null)}
        onOk={() => shipForm.submit()}
        confirmLoading={shipMutation.isPending}
        destroyOnHidden
      >
        <Form form={shipForm} layout="vertical" onFinish={(v) => shipMutation.mutate(v)}>
          <Form.Item name="company" label="快递公司" rules={[{ required: true, whitespace: true }]}>
            <Input maxLength={64} placeholder="如：顺丰速运" />
          </Form.Item>
          <Form.Item name="trackingNumber" label="快递单号" rules={[{ required: true, whitespace: true }]}>
            <Input maxLength={64} />
          </Form.Item>
          <Alert type="info" showIcon message="保存后客户在小程序订单详情里就能看到快递公司和单号。" />
        </Form>
      </Modal>
    </Card>
  );
}
